import React, { useEffect, useState } from 'react'
import axios from 'axios'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    MenuItem,
    Divider,
    IconButton,
    Box,
    Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import apiUrl from '../../configs/constant'
import { useToast } from '../../components/ToastProvider'

/* ===================== STATIC OPTIONS ===================== */

const jenisAktifitas = [
    { id: '1', label: 'Kontak' },
    { id: '2', label: 'Follow Up' },
    { id: '3', label: 'Order' }
]

const jenisKontak = [
    { id: '1', label: 'Whatsapp' },
    { id: '2', label: 'Telp' },
    { id: '3', label: 'Email' },
    { id: '4', label: 'Kunjungan' }
]

/* ===================== TYPES ===================== */

interface Props {
    open: boolean
    contact?: any
    onClose: () => void
}

/* ===================== COMPONENT ===================== */

export default function ActivityScheduleDialog({ open, contact, onClose }: Props) {
    const { showToast } = useToast()

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    const token = userData?.token

    const [activities, setActivities] = useState<any[]>([])
    const [editingId, setEditingId] = useState<number | null>(null)

    const [title, setTitle] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [activityType, setActivityType] = useState('')
    const [contactType, setContactType] = useState('')
    const [description, setDescription] = useState('')
    const [pic, setPic] = useState('')

    const [search, setSearch] = useState('')

    const [activeForm, setActiveForm] = useState(false)

    /* ===================== LOAD ACTIVITY ===================== */
    const loadActivities = async () => {
        if (!contact?.sdcust_id) return

        try {
            const res = await axios.get(
                `${apiUrl.apiUrl}/omni/activity/get-activity/${contact.sdcust_id}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            )

            if (res.data.success) {
                setActivities(res.data.data)
            }
        } catch {
            showToast('Gagal mengambil catatan', 'error')
        }
    }

    useEffect(() => {
        if (open) {
            loadActivities()
        }
    }, [open])

    /* default tgl akhir = tgl awal */
    useEffect(() => {
        // hanya untuk ADD mode
        if (!editingId && startDate && !endDate) {
            setEndDate(startDate)
        }
    }, [startDate, editingId])

    const resetForm = () => {
        setEditingId(null)
        setTitle('')
        setStartDate('')
        setEndDate('')
        setActivityType('')
        setContactType('')
        setDescription('')
        setPic('')
    }

    const toDatetimeLocal = (value?: string) => {
        if (!value) return ''
        return value.replace(' ', 'T').slice(0, 16)
    }

    const convertDatetime = (value: string) => {
        if (!value) return null
        return value.replace('T', ' ') + ':00'
    }

    const filteredActivities = activities.filter(item => {
        const keyword = search.toLowerCase()

        return (
            item.judul?.toLowerCase().includes(keyword) ||
            item.jenis_aktifitas?.toLowerCase().includes(keyword) ||
            item.jenis_kontak?.toLowerCase().includes(keyword)
        )
    })

    const formatDate = (value?: string) => {
        if (!value) return '-'
        const date = new Date(value.replace(' ', 'T'))
        return date.toLocaleString('id-ID', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    /* ===================== SAVE / UPDATE ===================== */
    const saveSchedule = async () => {
        try {
            const payload = {
                sdcust_id: contact.sdcust_id,
                judul: title,
                tgl_awal: convertDatetime(startDate),
                tgl_akhir: convertDatetime(endDate),
                jenis_aktifitas: activityType,
                jenis_kontak: contactType,
                keterangan: description,
                pic
            }

            let res

            if (editingId) {
                res = await axios.put(
                    `${apiUrl.apiUrl}/omni/activity/edit/${editingId}`,
                    payload,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                )
            } else {
                res = await axios.post(
                    `${apiUrl.apiUrl}/omni/activity/add`,
                    payload,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                )
            }

            if (res.data.success) {
                showToast(
                    editingId ? 'Activity diperbarui' : 'Activity ditambahkan',
                    'success'
                )
                resetForm()
                loadActivities()
                setActiveForm(false)
            }
        } catch {
            showToast('Gagal menyimpan Activity', 'error')
        }
    }

    /* ===================== EDIT ===================== */
    const editActivity = (row: any) => {
        setActiveForm(true)
        setEditingId(row.id)
        setTitle(row.judul)
        // setStartDate(row.tgl_awal)
        // setEndDate(row.tgl_akhir)
        setStartDate(toDatetimeLocal(row.tgl_awal))
        setEndDate(toDatetimeLocal(row.tgl_akhir))
        setActivityType(row.jenis_aktifitas)
        setContactType(row.jenis_kontak)
        setDescription(row.keterangan)
        setPic(row.pic)
    }

    /* ===================== DELETE ===================== */
    const deleteActivity = async (id: number) => {
        if (!confirm('Hapus activity ini?')) return

        try {
            const res = await axios.delete(
                `${apiUrl.apiUrl}/omni/activity/delete/${id}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            )

            if (res.data.success) {
                showToast('Activity dihapus', 'success')
                loadActivities()
                setActiveForm(false)
            }
        } catch {
            showToast('Gagal menghapus Activity', 'error')
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Jadwal Aktifitas</DialogTitle>

            <DialogContent dividers>
                {activeForm ? (
                    <Box>
                        <Button variant="contained" onClick={() => setActiveForm(false)}>
                            Kembali
                        </Button>
                    </Box>
                ) : (
                    <>
                        <TextField
                            size="small"
                            placeholder="Cari judul / aktifitas / kontak"
                            fullWidth
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        {/* === SCROLLABLE CONTENT === */}
                        <Box
                            sx={{
                                maxHeight: 500,
                                overflowY: 'auto',
                                pr: 1,
                                '&::-webkit-scrollbar': {
                                    width: 6
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#ccc',
                                    borderRadius: 3
                                }
                            }}
                        >
                            <Stack spacing={1}>
                                {filteredActivities.map(item => (
                                    <Stack
                                        key={item.id}
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        sx={{
                                            p: 1.5,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            backgroundColor: '#fafafa'
                                        }}
                                    >
                                        <Stack spacing={0.5}>
                                            <b>{item.judul}</b>

                                            <Typography variant="body2">
                                                Jenis Aktifitas : <b>{item.jenis_aktifitas || '-'}</b>
                                            </Typography>

                                            <Typography variant="body2">
                                                Jenis Kontak : <b>{item.jenis_kontak || '-'}</b>
                                            </Typography>

                                            <Typography variant="body2">
                                                PIC : <b>{item.pic || '-'}</b>
                                            </Typography>

                                            <Typography variant="body2">
                                                Tanggal : <b>{formatDate(item.tgl_awal)} – {formatDate(item.tgl_akhir)}</b>
                                            </Typography>
                                        </Stack>

                                        <Stack direction="row">
                                            <IconButton size="small" onClick={() => editActivity(item)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => deleteActivity(item.id)}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Stack>
                                    </Stack>
                                ))}

                                {filteredActivities.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Activity tidak ditemukan
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    </>
                )}


                <Divider sx={{ my: 2 }} />

                {/* ===== FORM ===== */}
                {activeForm ? (
                    <Stack spacing={2}>
                        <TextField label="Judul Aktifitas" fullWidth value={title} onChange={e => setTitle(e.target.value)} />

                        <TextField
                            type="datetime-local"
                            label="Tanggal Awal"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                        />

                        <TextField
                            type="datetime-local"
                            label="Tanggal Akhir"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                        />

                        <TextField select label="Jenis Aktifitas" fullWidth value={activityType}
                            onChange={e => setActivityType(e.target.value)}>
                            {jenisAktifitas.map(item => (
                                <MenuItem key={item.id} value={item.label}>{item.label}</MenuItem>
                            ))}
                        </TextField>

                        <TextField select label="Jenis Kontak" fullWidth value={contactType}
                            onChange={e => setContactType(e.target.value)}>
                            {jenisKontak.map(item => (
                                <MenuItem key={item.id} value={item.label}>{item.label}</MenuItem>
                            ))}
                        </TextField>

                        <TextField label="PIC" fullWidth value={pic} onChange={e => setPic(e.target.value)} />

                        <TextField
                            label="Keterangan"
                            multiline
                            rows={3}
                            fullWidth
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </Stack>
                ) : (
                    <Button variant="contained" onClick={() => {
                        resetForm()
                        setActiveForm(true)
                    }}>
                        Tambah Aktifitas
                    </Button>
                )}
            </DialogContent>

            {
                activeForm ? (
                    <DialogActions>
                        <Button onClick={resetForm}>Reset</Button>
                        <Button variant="contained" onClick={saveSchedule}>
                            {editingId ? 'Update' : 'Simpan'}
                        </Button>
                    </DialogActions>
                ) : ('')
            }
        </Dialog >
    )
}
